import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { GroupRemove, UploadFile } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import base_url from "../../../constants";

const CARD_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: 820,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
};

const WhatsappBusinessGroupDeleteContactsModal = ({
  open,
  onClose,
  groupName,
  onSuccess,
}) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(false);
  const notify = useNotification();

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError(false);
    }
  }, [open]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile || null);
    setFileError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupName) {
      notify("Group name is missing. Please try again.", "error");
      return;
    }
    if (!file) {
      setFileError(true);
      return;
    }

    const formData = new FormData();
    formData.append("group_name", groupName);
    formData.append("file", file);

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `${base_url}/whatsapp-business/groups/delete-contacts`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.status === "success" || res.data.success) {
        notify(
          res.data.message || "Contacts deleted successfully",
          "success"
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        notify(res.data.message || "No contacts deleted", "info");
      }
    } catch (err) {
      notify(
        err.response?.data?.message ||
          "Something went wrong while deleting contacts.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="delete-contacts-modal-title"
      aria-describedby="delete-contacts-modal-description"
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15,23,42,0.55)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={CARD_STYLE}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "rgba(248,113,113,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <GroupRemove sx={{ fontSize: 20, color: "#b91c1c" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Delete Contacts from Group
                      </Typography>
                    </Box>
                    <Button
                      onClick={onClose}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Upload a list of phone numbers to remove them from this
                    group.
                  </Typography>

                  {groupName && (
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 0.5 }}
                      >
                        Group:
                      </Typography>
                      <Chip
                        size="small"
                        label={groupName}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(248,113,113,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                      }}
                    >
                      <UploadFile sx={{ fontSize: 30, color: "#b91c1c" }} />
                    </Box>
                    <Button
                      type="button"
                      variant="outlined"
                      color="error"
                      sx={{ borderRadius: 2, mb: 1 }}
                      component="label"
                    >
                      <UploadFile sx={{ fontSize: 18, mr: 1 }} />
                      Choose Excel / CSV File
                      <input
                        type="file"
                        hidden
                        accept=".xls, .xlsx, .csv"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {file && (
                      <Typography variant="body2">
                        Selected file: {file.name}
                      </Typography>
                    )}
                    {fileError && (
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        Please select an Excel or CSV file
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        mt: 1.5,
                        fontSize: "12px",
                        color: "text.secondary",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>Note: </span>
                      Upload a sheet with a <code>phone_number</code> column.
                      Only matching numbers will be removed from this group.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      color="error"
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        position: "relative",
                      }}
                      disabled={loading}
                    >
                      Delete Contacts
                      {loading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      ) : null}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default WhatsappBusinessGroupDeleteContactsModal;

